<template>
  <form
    class="login-form"
    @submit.prevent="submit"
  >
    <div class="header">
      <AkHeading3 class="title">
        <AkIcon
          size="md"
          symbol="ankorstore-logo"
        />
        {{ $t('loginForm.title') }}
      </AkHeading3>
    </div>

    <div class="content">
      <div
        v-if="loading"
        class="content-loader"
      >
        <LoaderIcon />
      </div>
      <div
        v-else-if="forceReset"
        class="force-reset-info"
      >
        {{ $t('loginForm.error.forcePasswordReset') }}
      </div>
      <div
        v-else
        class="form"
      >
        <div class="login-form__input">
          <AkInput
            id="email"
            v-model="email"
            class="login-form__email"
            type="email"
            data-akt="login-email-input"
            data-testid="login-email-input"
            name="email"
            autocomplete="username"
            :placeholder="$t('loginForm.adressPlaceholder')"
            :label="$t('loginForm.adressLabel')"
            required
          />
        </div>
        <div class="login-form__input">
          <AkInput
            id="password"
            v-model="password"
            class="login-form__password"
            type="password"
            data-testid="login-password-input"
            name="password"
            autocomplete="current-password"
            :placeholder="$t('loginForm.passwordPlaceholder')"
            :label="$t('loginForm.passwordLabel')"
            with-password-eye
            required
          />
        </div>
        <div
          v-if="!loading"
          class="pwd"
        >
          <AkButton
            class="forgot-pwd"
            link
            size="md"
            data-testid="loginPasswordForgottenLink"
            @click.prevent="$emit('change-to-reset-password')"
          >
            {{ $t('loginForm.resetPasswordLabel') }}
          </AkButton>
        </div>
      </div>

      <div
        v-if="errorMessage"
        class="global-error"
      >
        {{ errorMessage }}
      </div>
      <AkButton
        v-if="!forceReset"
        type="submit"
        class="button--extended ds-mt-3"
        size="xl"
        data-akt="login-submit-button"
        @submit="submit"
      >
        {{ $t('loginForm.loginButton') }}
      </AkButton>

      <div
        v-if="!forceReset && isEnabled('POC-google-register')"
        class="google-signin"
      >
        <div class="google-signin__divider">
          <span class="google-signin__divider-text">{{ $t('or') }}</span>
        </div>
        <AkButton
          type="button"
          size="lg"
          class="google-signin__button"
          data-testid="google-sign-in-button"
          :disabled="googleLoading"
          @click="onGoogleSignIn"
        >
          <svg
            class="google-signin__icon"
            viewBox="0 0 24 24"
            width="20"
            height="20"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.98 10.98 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>{{ $t('loginForm.google.button') }}</span>
        </AkButton>
        <div
          id="google-signin-hidden"
          style="position: absolute; opacity: 0; pointer-events: none; height: 0; overflow: hidden;"
        />
      </div>
    </div>

    <div
      v-if="!forceReset"
      class="footer"
    >
      <AkButton
        link
        data-akt="login-change-to-register-link"
        @click.prevent="$emit('change-to-register')"
      >
        {{ $t('No account yet?') }} {{ $t('Register now') }}
      </AkButton>
    </div>
  </form>
</template>

<script lang="ts">
import http from '@monolith/legacy/services/api/http';
import { AkButton, AkInput, AkHeading3 } from '@ankorstore/design-system';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import { defineComponent } from 'vue';
import { FeatureFlag, isEnabled } from '@monolith/legacy/services/features';
import { useGoogleSignIn } from '@monolith/legacy/composables/use-google-sign-in';
import type { PropType } from 'vue';
import type { SignupAudience } from '@monolith/legacy/types/signup-audience';

export default defineComponent({
  name: 'LoginForm',
  components: { AkButton, LoaderIcon, AkInput, AkHeading3 },
  props: {
    prefilledEmail: {
      type: String,
      required: false,
      default: '',
    },
    deviceId: {
      type: String,
      required: false,
      default: '',
    },
    // Routing input, not analytics context: logging in has no audience — the
    // account already knows what it is. But a Google sign-in here that finds no
    // account becomes a signup, and this decides which funnel it lands in. A
    // brand applicant reaches this form from the brand email step via "Already
    // have an account?", and must be sent back to the brand funnel.
    audience: {
      type: String as PropType<SignupAudience>,
      required: true,
    },
  },
  emits: ['change-to-register', 'change-to-reset-password', 'success', 'change-to-two-factor'],
  setup(props) {
    // Deliberately only the audience: source/is_personalized/is_reminded describe
    // the signup form's context (the lift personalization block this form never
    // renders), so they have no meaning on a login.
    const { googleLoading, onGoogleSignIn, handleGoogleCredentialResponse } = useGoogleSignIn(() => ({
      audience: props.audience,
    }));
    return { googleLoading, onGoogleSignIn, handleGoogleCredentialResponse };
  },
  data: () => {
    return {
      email: '',
      password: '',
      errorMessage: '',
      loading: false,
      forceReset: false,
    };
  },
  /** On mounted, set email from prefilledEmail */
  beforeMount() {
    this.email = this.prefilledEmail;
    if (document.querySelector('meta[name="force_password_reset"]')) {
      this.forceReset = true;
    }
  },
  methods: {
    isEnabled,
    close() {
      this.$parent.$parent.hide();
    },
    getErrorMessage(code) {
      switch (code) {
        case 'invalid_grant':
          this.errorMessage = this.$t('loginForm.error.invalidCredentials');
          break;
        case 'force_password_reset':
          this.forceReset = true;
          break;
        default:
          this.errorMessage = this.$t('loginForm.error.generic');
      }
    },
    submit() {
      if (!this.email || !this.password) {
        this.errorMessage = this.$t('loginForm.missingFields');
        return;
      }
      this.$store.commit('SET_PROGRESS_BAR_STATE', 'loading');
      this.errorMessage = '';
      this.forceReset = false;
      this.loading = true;
      const requestConfig = isEnabled(FeatureFlag.Ravelin)
        ? {
            headers: {
              'X-Device-Id': this.deviceId,
            },
          }
        : {};
      http()
        .post(
          '/auth/login',
          {
            email: this.email,
            password: this.password,
          },
          requestConfig
        )
        .then((response) => {
          this.loading = false;
          const twoFactorHeader = response.headers['x-aks-2fa-status'] ?? '';
          if (twoFactorHeader === 'required' || response.data?.two_factor) {
            this.$emit('change-to-two-factor');
          } else {
            this.$emit('success', response);
          }
        })
        .catch((error) => {
          this.loading = false;
          this.getErrorMessage(error.response.data?.error);
        })
        .finally(() => {
          this.$store.commit('SET_PROGRESS_BAR_STATE', 'ending');
        });
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@css/vue-import';

.content-loader {
  @apply ds-flex ds-justify-center;
  min-height: 115px;
  align-items: center;
}

.force-reset-info {
  @apply ds-flex ds-items-center ds-justify-center ds-text-center ds-text-sm ds-py-4;
  min-height: 115px;
}

.login-form {
  .header {
    @apply ds-mb-6;
  }
  &__input {
    @apply ds-mb-4;
  }
  .pwd {
    @apply ds-mb-[56px];
  }
}

.button {
  &__submit {
    @apply ds-text-base;
  }
}

.google-signin {
  @apply ds-mt-2;

  &__divider {
    @apply ds-relative ds-flex ds-items-center ds-justify-center ds-my-3;

    &::before,
    &::after {
      content: '';
      @apply ds-flex-1 ds-border-t ds-border-neutral-300;
    }
  }

  &__divider-text {
    @apply ds-px-3 ds-text-sm ds-text-neutral-600;
  }

  &__button {
    @apply ds-w-full ds-flex ds-items-center ds-justify-center ds-gap-3 #{!important};
    @apply ds-rounded-full #{!important};
    @apply ds-border ds-border-neutral-300 ds-bg-white #{!important};
    @apply ds-text-sm ds-font-medium ds-text-neutral-900 #{!important};
    @apply ds-cursor-pointer ds-transition-colors #{!important};

    &:hover {
      @apply ds-bg-neutral-100 #{!important};
    }

    &:disabled {
      @apply ds-opacity-50 ds-cursor-not-allowed #{!important};
    }
  }

  &__icon {
    @apply ds-flex-shrink-0;
  }
}

.footer {
  @apply ds-flex ds-justify-center ds-mt-4;
}
</style>
